import AppContext from '~/constants/appContext';
import { ClientId } from '~/constants/Events';
import { InventoryTypeKey } from '~/constants/inventoryType';
import type {
  Purchasing,
  ReduxStore,
  SpinnerType,
  UserFinancing
} from '~/contracts/vdp';
import { getAssociatedVdpEventId, getVehicleDetails } from '~/selectors';
import type { DeliveryPickupResponseContent } from '~/services/common/contracts';
import {
  fetchDeliveryPickupData,
  fetchRecallStatus,
  fetchReviewsCount,
  fetchTransitChargeAnonymous,
  fetchTransitChargeAuthenticated,
  fetchUserFinancing,
  fetchUserPurchasing,
  fetchUserVehicleAvailability
} from '~/services/vdp';
import initialState from '~/store/initialState';
import generateErrorLogger, { toError } from '~/utilities/errorHandlers';
import * as types from './actionTypes';

export const getUserVehicleAvailability =
  ({ userId, vehicleId, zip }) =>
  dispatch =>
    dispatch({
      type: types.GET_USER_AVAILABILITY,
      payload: () =>
        fetchUserVehicleAvailability({
          userId,
          vehicleId,
          zip
        })
    }).catch(generateErrorLogger('Get vehicle availability'));

export const getUserPurchasing = () => dispatch =>
  dispatch({
    type: types.GET_USER_PURCHASING,
    async payload(): Promise<Purchasing | null> {
      const logger = generateErrorLogger('Get user purchasing');

      try {
        const details = await fetchUserPurchasing();

        return details.hasActiveCart
          ? {
              inventoryType: details.inventoryType,
              make: details.make,
              model: details.model,
              orderState: details.orderState,
              stockNumber: details.stockNumber,
              vehicleId: details.vehicleId,
              year: details.year
            }
          : null;
      } catch (e) {
        logger(toError(e));
      }

      return null;
    }
  });

export const spinnerFailedLoading = (hasError: boolean) => ({
  type: types.SPINNER_FAILED_LOADING,
  payload: hasError
});

export const getRecallStatus = (vehicleId: number) => dispatch =>
  dispatch({
    type: types.GET_RECALL_STATUS,
    async payload() {
      const { data } = await fetchRecallStatus(vehicleId);

      return data;
    }
  }).catch(generateErrorLogger('Get recall status'));

export const getDeliveryPickupInfo =
  (
    clientId: ClientId,
    userId: string,
    zip: string,
    controller: AbortController
  ) =>
  (dispatch, getState: () => ReduxStore) => {
    const associatedVdpEventId = getAssociatedVdpEventId(getState());
    const {
      inventoryType = InventoryTypeKey.Core,
      location: { locationId = 0 } = {},
      saleStatus = '',
      vehicleId = 0
    } = getVehicleDetails(getState());

    return dispatch({
      type: types.GET_DELIVERY_PICKUP_DATA,
      async payload(): Promise<DeliveryPickupResponseContent> {
        const { data } = await fetchDeliveryPickupData(
          {
            clientId,
            correlationId: associatedVdpEventId,
            inventoryType,
            locationId,
            saleStatus,
            userId,
            vehicleId,
            zip
          },
          controller
        );

        if (!data.content) {
          throw new Error(data.errorMessage);
        }

        return data.content[0];
      }
    }).catch(generateErrorLogger('Get delivery & pickup info'));
  };

export const getTransitCharge =
  (
    controller: AbortController,
    isLoggedIn: boolean,
    stateAbbreviation: string,
    vehicleId: number,
    zip: string,
    routeId?: string
  ) =>
  (dispatch: any, getState: () => ReduxStore) =>
    dispatch({
      type: types.GET_TRANSIT_CHARGE,
      async payload() {
        const associatedVdpEventId = getAssociatedVdpEventId(getState());

        const quote = await (isLoggedIn
          ? fetchTransitChargeAuthenticated(
              controller,
              { routeId, stateAbbreviation, vehicleId },
              associatedVdpEventId
            )
          : fetchTransitChargeAnonymous(
              controller,
              { deliveryZip5: zip, routeId, vehicleId },
              associatedVdpEventId
            ));

        dispatch({
          type: types.GET_TRANSIT_ROUTE,
          payload: {
            transitActivityType: quote.transferType,
            transitRouteId: quote.routeId
          }
        });

        return quote.deliveryCharge;
      }
    }).catch(generateErrorLogger('Get transit charge'));

export const getHasOwnerReviews = (dispatch, getState: () => ReduxStore) =>
  dispatch({
    type: types.GET_HAS_OWNER_REVIEWS,
    async payload() {
      const {
        make = '',
        model = '',
        trim = '',
        year = 0
      } = getVehicleDetails(getState());

      const { data } = await fetchReviewsCount(make, model, trim, year);

      return data.summary.review_count_total > 0;
    }
  }).catch(generateErrorLogger('Get has reviews'));

export const getUserFinancing = (userId: string) => dispatch =>
  dispatch({
    type: types.GET_USER_FINANCING,
    payload: (): Promise<UserFinancing | null> =>
      userId
        ? fetchUserFinancing(userId)
        : new Promise<UserFinancing | null>(resolve =>
            resolve(initialState.user.financing.data)
          )
  });

export const setSpinnerType = (payload: SpinnerType) => ({
  type: types.SET_SPINNER_TYPE,
  payload
});

export const setSpinnerScriptInserted = () => ({
  type: types.SET_SPINNER_SCRIPT_INSERTED
});

export const setSpinnerImmersive = (payload: boolean) => ({
  type: types.SET_SPINNER_IMMERSIVE,
  payload
});

export const setSpinnerTourMode = (payload: boolean) => ({
  type: types.SET_SPINNER_TOUR_MODE,
  payload
});

export const addExperiment = ({
  assignedBucket,
  experimentName
}: {
  assignedBucket?: string;
  experimentName: string;
}) => ({
  type: types.ADD_EXPERIMENT,
  payload: {
    [experimentName]: {
      assignedBucket
    }
  }
});

export const setExperimentsLoaded = (context: AppContext) => {
  const contextKey: keyof ReduxStore['experimentation'] =
    context === AppContext.Client ? 'isClientLoaded' : 'isServerLoaded';

  return {
    type: types.SET_EXPERIMENTS_LOADED,
    payload: { [contextKey]: true }
  };
};
